'use client';

import { useRef, useEffect } from 'react';
import { useBookmarkStore } from '@/stores/bookmark-store';
import { useCalendarStore } from '@/stores/calendar-store';
import { useAuth } from '@/hooks/useAuth';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { uiFb } from '@/lib/i18n';
import { getLocale } from '@/lib/date-format';
import { Flag } from '@/components/ui/Flag';

interface BookmarksPanelProps {
  open: boolean;
  onClose: () => void;
}

export function BookmarksPanel({ open, onClose }: BookmarksPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const lang = useCalendarStore((s) => s.lang);
  const bookmarks = useBookmarkStore((s) => s.bookmarks);
  const toggleBookmark = useBookmarkStore((s) => s.toggleBookmark);
  useFocusTrap(panelRef, open);

  const sorted = [...bookmarks.values()].sort((a, b) =>
    (a.event_date_start ?? '').localeCompare(b.event_date_start ?? '')
  );

  useEffect(() => {
    if (!open) return;
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      ref={panelRef}
      className="fixed inset-0 z-[100] flex justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Saved events"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-[var(--overlay-scrim)] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="relative w-full max-w-sm flex flex-col bg-surface-sidebar border-l border-border-primary shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border-subtle px-4 pt-4 pb-3">
          <h2 className="font-heading text-lg font-bold text-text-primary">{uiFb('ui.bookmark.title', 'Saved Events', lang)}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-muted transition-colors hover:bg-surface-hover hover:text-text-secondary"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="text-muted mb-3">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
              <p className="text-sm text-text-secondary">{uiFb('ui.bookmark.empty', 'No saved events yet', lang)}</p>
              <p className="mt-1 text-xs text-muted">{uiFb('ui.bookmark.hint', 'Tap the heart icon on any event to save it here', lang)}</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {sorted.map((b) => {
                const dateLabel = b.event_date_start
                  ? new Date(b.event_date_start + 'T00:00:00').toLocaleDateString(getLocale(lang), { month: 'short', day: 'numeric' })
                  : '';

                return (
                  <li key={b.event_slug} className="flex items-center gap-3 content-card p-3">
                    {b.event_country && <Flag code={b.event_country} size={16} className="shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <a
                        href={`/events/${b.event_slug}`}
                        className="block truncate text-sm font-medium text-text-primary hover:underline"
                      >
                        {b.event_title}
                      </a>
                      {dateLabel && (
                        <span className="text-xs text-muted">{dateLabel}</span>
                      )}
                    </div>
                    <button
                      onClick={() => user && toggleBookmark(user.id, {
                        event_slug: b.event_slug,
                        event_title: b.event_title,
                        event_date_start: b.event_date_start ?? undefined,
                        event_country: b.event_country ?? undefined,
                      })}
                      className="shrink-0 rounded p-1 text-semantic-error transition-colors hover:bg-semantic-error/10"
                      aria-label={`Remove ${b.event_title} from saved`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
